import { ColumnDef } from '@tanstack/react-table';
import { ReadLeadFormSubmission } from '@tinct/core-api-models';
import {
  BadgeStatus,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@tinct/web-components';
import { CollectionColumnHeader } from '@tinct/web-collection';
import { format } from 'date-fns';

/**
 * Columns for lead-form submission lists — shared by the campaign's Leads
 * tab and the workspace-wide list.
 */
export function useLeadFormSubmissionsColumns(): ColumnDef<ReadLeadFormSubmission>[] {
  return [
    {
      accessorKey: 'email',
      header: ({ column }) => (
        <CollectionColumnHeader column={column} title="Email" />
      ),
      cell: ({ row }) => (
        <span className="font-medium">{row.getValue('email') ?? '-'}</span>
      ),
    },
    {
      accessorKey: 'data',
      header: () => <span>Details</span>,
      enableSorting: false,
      cell: ({ row }) => {
        const data = row.original.data;
        if (!data) return <span className="text-muted-foreground">-</span>;
        // Everything but the email (already its own column), joined compactly;
        // the full breakdown lives in the tooltip.
        const entries = Object.entries(data).filter(([key]) => key !== 'email');
        if (entries.length === 0)
          return <span className="text-muted-foreground">-</span>;
        const summary = entries.map(([, value]) => value).join(' · ');
        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="block max-w-[320px] truncate text-muted-foreground">
                {summary}
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-sm whitespace-pre-wrap break-words">
              {entries.map(([key, value]) => `${key}: ${value}`).join('\n')}
            </TooltipContent>
          </Tooltip>
        );
      },
      meta: { className: 'max-w-[320px]' },
    },
    {
      accessorKey: 'forwardStatus',
      header: ({ column }) => (
        <CollectionColumnHeader column={column} title="Status" />
      ),
      cell: ({ row }) => {
        const status = row.original.forwardStatus;
        if (status === 'stored')
          return <BadgeStatus variant="blue">Stored</BadgeStatus>;
        if (status === 'forwarded')
          return <BadgeStatus variant="green">Forwarded</BadgeStatus>;
        if (status === 'forward_failed') {
          const error = row.original.forwardError;
          const badge = <BadgeStatus variant="red">Forward failed</BadgeStatus>;
          if (!error) return badge;
          return (
            <Tooltip>
              <TooltipTrigger asChild>
                <span>{badge}</span>
              </TooltipTrigger>
              <TooltipContent className="max-w-sm whitespace-pre-wrap break-words">
                {error}
              </TooltipContent>
            </Tooltip>
          );
        }
        return <span className="text-muted-foreground">-</span>;
      },
      filterFn: 'equals',
    },
    {
      accessorKey: 'sourceUrl',
      header: () => <span>Source</span>,
      enableSorting: false,
      cell: ({ row }) => {
        const url = row.original.sourceUrl;
        if (!url) return <span className="text-muted-foreground">-</span>;
        return (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="block max-w-[240px] truncate text-muted-foreground underline underline-offset-2 hover:text-foreground"
          >
            {url}
          </a>
        );
      },
      meta: { className: 'max-w-[240px]' },
    },
    {
      accessorKey: 'createdAt',
      header: ({ column }) => (
        <CollectionColumnHeader column={column} title="Date" />
      ),
      cell: ({ row }) => {
        const createdAt = row.original.createdAt;
        if (!createdAt) return <span className="text-muted-foreground">-</span>;
        return (
          <span className="text-muted-foreground">
            {format(new Date(createdAt), 'PP p')}
          </span>
        );
      },
    },
  ];
}
